import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InfluencerLogin() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = search.get('token');
  const [state, setState] = useState({ loading: true, ok: false, error: null });
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, error: null });
      return;
    }
    (async () => {
      try {
        await axios.get(`${API}/api/influencer/login?token=${encodeURIComponent(token)}`, { withCredentials: true });
        setState({ loading: false, ok: true, error: null });
        setTimeout(() => navigate('/influencer/dashboard'), 800);
      } catch (err) {
        setState({ loading: false, ok: false, error: err.response?.data?.detail || 'Login link invalid' });
      }
    })();
  }, [token, navigate]);

  const requestLink = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/api/influencer/login-request`, { email });
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  if (token) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          {state.loading && <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF] mx-auto mb-4" />}
          {state.ok && <CheckCircle2 className="w-12 h-12 text-[#00F0FF] mx-auto mb-4" />}
          <p className="text-neutral-300">
            {state.loading && 'Signing you in…'}
            {state.ok && 'Signed in — taking you to your dashboard…'}
            {state.error && <span className="text-red-400">{state.error}</span>}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="influencer-login">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5">
          <Link to="/" className="text-[#00F0FF] font-bold tracking-tight">theailedger.ai</Link>
        </div>
      </header>

      <section className="max-w-md mx-auto px-6 pt-16">
        <h1 className="text-3xl font-bold mb-3">Influencer dashboard</h1>
        <p className="text-neutral-400 mb-6 text-sm leading-relaxed">
          First time here? Just enter the email theailedger.ai used to set you up. We&apos;ll send
          a one-tap login link — no password needed.
        </p>

        {sent ? (
          <div className="bg-neutral-900/60 border border-white/10 rounded-xl p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-[#00F0FF] mx-auto mb-3" />
            <p className="text-neutral-200">Check your email — link is good for 15 minutes.</p>
          </div>
        ) : (
          <form onSubmit={requestLink} className="bg-neutral-900/40 border border-white/10 rounded-xl p-5 space-y-4">
            <label className="block">
              <span className="block text-sm font-semibold mb-1.5">Email</span>
              <input required type="email" value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     data-testid="login-email"
                     className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
            </label>
            <button type="submit" disabled={sending}
                    data-testid="login-submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#00F0FF]/90 transition disabled:opacity-50">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {sending ? 'Sending…' : 'Send me a login link'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
